import { endpoints } from '@/api/endpoints';
import { apiClient } from '@/api/axios';
import type {
  AppShellEnvironment,
  AppShellLanguage,
  AppShellTheme,
  AppShellUserProfile,
} from '@/shell/AppShellContext';
import { normalizeEnvironmentName } from '@/config/urls';
import { normalizeStoredLocale } from '@/i18n/languageCodes';
import { resolveDeploymentEnvironment } from '@/shell/runtimeEnvironment';
import type { AuthUser } from '@/types/auth';

const profileStorageKey = 'entity_portal_profile_preferences';
const profilePermissionsKey = 'entity_portal_profile_permissions';
const delayMs = 180;

function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(value), delayMs);
  });
}

interface StoredPreferences {
  preferredLanguage: AppShellLanguage;
  theme: AppShellTheme;
  environment: AppShellEnvironment;
}

interface UserProfileResponse {
  user_id: string;
  email?: string | null;
  name?: string | null;
  groups: string[];
  permissions: string[];
}

const adminPortalPermissionSeeds = new Set<string>([
  'USERS_READ',
  'GROUPS_READ',
  'ROLES_READ',
  'PRODUCTS_READ',
  'PRODUCT_MENU_CONFIGURATION_READ',
  'NOTIFICATIONS_READ',
  'AUDIT_LOGS_READ',
  'FEATURE_FLAGS_READ',
  'SYSTEM_SETTINGS_READ',
  'API_CONFIGURATION_READ',
  'DOCUMENTATION_HUB_READ',
]);

function normalizePreferenceEnvironment(
  environment: string | undefined,
): AppShellEnvironment {
  return normalizeEnvironmentName(environment);
}

function mapPermissionResource(resource: string): string {
  const normalized = resource.trim().toLowerCase();

  if (normalized === 'users') {
    return 'USERS';
  }

  if (normalized === 'groups') {
    return 'GROUPS';
  }

  if (normalized === 'roles') {
    return 'ROLES';
  }

  if (normalized === 'products') {
    return 'PRODUCTS';
  }

  if (normalized === 'menus') {
    return 'PRODUCT_MENU_CONFIGURATION';
  }

  if (normalized === 'notifications') {
    return 'NOTIFICATIONS';
  }

  if (normalized === 'audit' || normalized === 'audit_logs') {
    return 'AUDIT_LOGS';
  }

  if (normalized === 'feature_flags' || normalized === 'featureflags') {
    return 'FEATURE_FLAGS';
  }

  if (normalized === 'system' || normalized === 'system_settings') {
    return 'SYSTEM_SETTINGS';
  }

  if (normalized === 'api' || normalized === 'api_configuration') {
    return 'API_CONFIGURATION';
  }

  if (
    normalized === 'documentation' ||
    normalized === 'documentation_hub' ||
    normalized === 'docs'
  ) {
    return 'DOCUMENTATION_HUB';
  }

  if (normalized === 'embedded_applications' || normalized === 'embedded') {
    return 'EMBEDDED_APPLICATIONS';
  }

  return normalized.replace(/[^a-z0-9]+/g, '_').toUpperCase();
}

function mapPermissionAction(action: string): string {
  const normalized = action.trim().toLowerCase();

  if (normalized === 'read' || normalized === 'view') {
    return 'READ';
  }

  if (normalized === 'create') {
    return 'CREATE';
  }

  if (normalized === 'update' || normalized === 'edit') {
    return 'UPDATE';
  }

  if (normalized === 'delete') {
    return 'DELETE';
  }

  if (normalized === 'write' || normalized === 'admin' || normalized === '*') {
    return 'ADMIN';
  }

  return normalized.replace(/[^a-z0-9]+/g, '_').toUpperCase();
}

function expandPermission(permission: string): string[] {
  const normalized = permission.trim();

  if (!normalized) {
    return [];
  }

  if (normalized === '*') {
    return ['*'];
  }

  if (!normalized.includes(':')) {
    return [normalized.toUpperCase()];
  }

  const [resourcePart, remainder] = normalized.split(':', 2);
  const resourceToken = mapPermissionResource(resourcePart);
  const actionSegment = remainder.includes('.')
    ? remainder.slice(remainder.lastIndexOf('.') + 1)
    : remainder;
  const actionToken = mapPermissionAction(actionSegment);

  if (actionToken === 'ADMIN') {
    return [
      `${resourceToken}_READ`,
      `${resourceToken}_CREATE`,
      `${resourceToken}_UPDATE`,
      `${resourceToken}_DELETE`,
      `${resourceToken}_ADMIN`,
    ];
  }

  return [`${resourceToken}_${actionToken}`];
}

function normalizePortalPermissions(permissions: string[]): string[] {
  const normalized = new Set<string>();

  permissions.forEach((permission) => {
    expandPermission(permission).forEach((token) => normalized.add(token));
  });

  return [...normalized];
}

function buildDefaultPreferences(): StoredPreferences {
  return {
    preferredLanguage: 'en-US',
    environment: resolveDeploymentEnvironment(),
    theme: 'system',
  };
}

function mapUserProfileResponseToAppShell(
  profile: UserProfileResponse,
  authUser?: AuthUser | null,
): AppShellUserProfile {
  const preferences = readStoredPreferences() ?? buildDefaultPreferences();
  const emailFromAuth = authUser?.email?.trim() ?? '';
  const emailFromProfile = profile.email?.trim() ?? '';
  const email = emailFromAuth || emailFromProfile;
  const displayNameFromAuth = authUser?.displayName?.trim() ?? '';
  const displayNameFromProfile = profile.name?.trim() ?? '';
  const displayName =
    displayNameFromAuth || displayNameFromProfile || email || profile.user_id;
  const normalizedPermissions = normalizePortalPermissions(profile.permissions);
  const hasAdministrativePermission = normalizedPermissions.includes('*');

  const resolvedPermissions = new Set<string>(normalizedPermissions);
  const hasAdminPortalSeedPermission = normalizedPermissions.some(
    (permission) =>
      permission.endsWith('_ADMIN') ||
      adminPortalPermissionSeeds.has(permission),
  );

  if (hasAdministrativePermission || hasAdminPortalSeedPermission) {
    resolvedPermissions.add('ADMIN_PORTAL_ACCESS');
  }

  if (
    hasAdministrativePermission ||
    resolvedPermissions.has('SYSTEM_SETTINGS_ADMIN')
  ) {
    resolvedPermissions.add('CHANGE_ENVIRONMENT');
  }

  return {
    id: profile.user_id,
    displayName,
    email,
    avatar: authUser?.photoUrl,
    roles: profile.groups,
    permissions: [...resolvedPermissions],
    preferredLanguage: preferences.preferredLanguage,
    theme: preferences.theme,
    environment: preferences.environment,
    status: 'online',
  };
}

function readStoredPreferences(): StoredPreferences | null {
  try {
    const value = window.localStorage.getItem(profileStorageKey);

    if (!value) {
      return null;
    }

    const parsed = JSON.parse(value) as Partial<StoredPreferences>;

    return {
      preferredLanguage: normalizeStoredLocale(parsed.preferredLanguage),
      theme: parsed.theme ?? 'system',
      environment: normalizePreferenceEnvironment(parsed.environment),
    };
  } catch {
    return null;
  }
}

function writeStoredPreferences(preferences: StoredPreferences): void {
  try {
    window.localStorage.setItem(profileStorageKey, JSON.stringify(preferences));
  } catch {
    // Ignore storage failures.
  }
}

function readStoredPermissions(): string[] {
  try {
    const value = window.localStorage.getItem(profilePermissionsKey);

    if (!value) {
      return [];
    }

    const parsed = JSON.parse(value) as string[];
    return normalizePortalPermissions(parsed);
  } catch {
    return [];
  }
}

function writeStoredPermissions(permissions: string[]): void {
  try {
    window.localStorage.setItem(
      profilePermissionsKey,
      JSON.stringify(permissions),
    );
  } catch {
    // Ignore storage failures.
  }
}

export const userProfileService = {
  // Placeholder for GET /me (uses /users/me when available)
  getMe: async (
    authUser?: AuthUser | null,
  ): Promise<AppShellUserProfile | null> => {
    try {
      const response = await apiClient.get<UserProfileResponse>(
        endpoints.userMe,
      );
      const profile = mapUserProfileResponseToAppShell(response.data, authUser);
      writeStoredPermissions(profile.permissions);
      return profile;
    } catch {
      return delay(null);
    }
  },

  // Placeholder for PUT /me/preferences
  updatePreferences: async (preferences: StoredPreferences): Promise<void> => {
    writeStoredPreferences({
      ...preferences,
      environment: normalizePreferenceEnvironment(preferences.environment),
    });
    await delay(undefined);
  },

  // Placeholder for PUT /me/theme
  updateTheme: async (theme: AppShellTheme): Promise<void> => {
    const current = readStoredPreferences() ?? buildDefaultPreferences();

    writeStoredPreferences({ ...current, theme });
    await delay(undefined);
  },

  // Placeholder for PUT /me/language
  updateLanguage: async (language: AppShellLanguage): Promise<void> => {
    const current = readStoredPreferences() ?? buildDefaultPreferences();

    writeStoredPreferences({ ...current, preferredLanguage: language });
    await delay(undefined);
  },

  // Placeholder for POST /logout
  logout: async (): Promise<void> => {
    await delay(undefined);
  },

  // Placeholder for GET /me/permissions
  getPermissions: async (): Promise<string[]> => {
    try {
      const response = await apiClient.get<UserProfileResponse>(
        endpoints.userMe,
      );
      const permissions = mapUserProfileResponseToAppShell(
        response.data,
      ).permissions;
      writeStoredPermissions(permissions);
      return permissions;
    } catch {
      return delay(readStoredPermissions());
    }
  },

  savePermissions: async (permissions: string[]): Promise<void> => {
    writeStoredPermissions(permissions);
    await delay(undefined);
  },

  readPreferences: (): StoredPreferences | null => {
    return readStoredPreferences();
  },
};
